import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { analyticsApi, topicProgressApi, competitiveExamsApi } from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Sun, Moon, TrendingUp, Target, Trophy, Flame,
  Loader2, BookOpen, CheckCircle2, Activity, Zap, BookX,
} from 'lucide-react';

export default function PerformanceHub() {
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [exams, setExams] = useState([]);
  const [examId, setExamId] = useState('all');
  const [overview, setOverview] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    competitiveExamsApi.getAll().then(setExams).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, pr] = await Promise.all([
        analyticsApi.getCompetitiveOverview(examId === 'all' ? null : examId, 30),
        examId !== 'all' ? topicProgressApi.get(examId).catch(() => null) : Promise.resolve(null),
      ]);
      setOverview(ov);
      setProgress(pr);
    } catch {
      toast.error('Failed to load analytics');
    }
    setLoading(false);
  }, [examId]);

  useEffect(() => { load(); }, [load]);

  const s = overview?.summary || {};
  const weak = overview?.weak_chapters || [];
  const trend = overview?.daily_trend || [];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="border-b border-border bg-card/50 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="perf-back">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-indigo-500" />Performance Hub
              </h1>
              <p className="text-xs text-muted-foreground">Your weak spots, your progress — everything in one dashboard.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/error-book')} data-testid="perf-open-errorbook">
              <BookX className="w-4 h-4 mr-1.5" />Error Book
            </Button>
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="perf-theme-toggle">
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={logout}>Logout</Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        {/* Exam selector */}
        <div className="flex items-center gap-3" data-testid="perf-filters">
          <span className="text-sm font-semibold">Exam:</span>
          <Select value={examId} onValueChange={setExamId}>
            <SelectTrigger className="w-64" data-testid="perf-exam-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All competitive exams</SelectItem>
              {exams.map(e => (
                <SelectItem key={e.exam_id} value={e.exam_id}>{e.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="perf-summary">
              <SummaryCard icon={Activity} color="indigo" label="Total attempts" value={s.total_attempts || 0} testId="summary-total" />
              <SummaryCard icon={Target} color="emerald" label="Avg. score" value={`${s.average_percentage ?? 0}%`} testId="summary-avg" />
              <SummaryCard icon={Trophy} color="amber" label="Best score" value={`${s.best_percentage ?? 0}%`} testId="summary-best" />
              <SummaryCard icon={Flame} color="rose" label="Last 30 days" value={s.recent_attempts || 0} testId="summary-recent" />
            </div>

            {/* Subject progress — only shown when a specific exam is picked */}
            {progress && (
              <div className="p-5 rounded-2xl border border-border bg-card" data-testid="perf-syllabus-progress">
                <h2 className="font-bold mb-4 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-indigo-500" />Syllabus Progress — {progress.exam_id}
                </h2>
                <div className="mb-4 p-3 rounded-xl bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-950/30 dark:to-violet-950/30 border border-indigo-200 dark:border-indigo-900" data-testid="perf-overall-progress">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold">Overall</span>
                    <span className="text-xs text-muted-foreground">
                      {progress.overall.studied}/{progress.overall.total} studied ({progress.overall.studied_pct}%)
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-background overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-indigo-500 to-violet-500" style={{ width: `${progress.overall.studied_pct}%` }} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {progress.subjects.map((sub, i) => (
                    <div key={sub.subject} className="p-3 rounded-xl border border-border" data-testid={`subject-progress-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold">{sub.subject}</p>
                        <Badge variant="outline" className="text-[10px]">{sub.studied}/{sub.total_topics}</Badge>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden mb-1.5">
                        <div className="h-full bg-emerald-500" style={{ width: `${sub.studied_pct}%` }} />
                      </div>
                      <div className="flex justify-between text-[10px] text-muted-foreground">
                        <span>Studied {sub.studied_pct}%</span>
                        <span>Practiced {sub.practiced_pct}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Weak chapters */}
            {weak.length > 0 && (
              <div className="p-5 rounded-2xl border border-rose-200 dark:border-rose-900 bg-gradient-to-br from-rose-50 to-orange-50 dark:from-rose-950/20 dark:to-orange-950/20" data-testid="perf-weak-chapters">
                <h2 className="font-bold mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4 text-rose-500" />Weak Chapters — based on your errors
                </h2>
                <div className="space-y-2">
                  {weak.map((w, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-card border border-rose-100 dark:border-rose-900" data-testid={`weak-chapter-${i}`}>
                      <div className="flex-1">
                        <p className="text-sm font-semibold">{w.topic}</p>
                        <p className="text-[11px] text-muted-foreground">{w.subject}</p>
                      </div>
                      <div className="h-2 w-32 rounded-full bg-rose-100 dark:bg-rose-950/40 overflow-hidden">
                        <div className="h-full bg-rose-500" style={{ width: `${Math.min(w.errors * 20, 100)}%` }} />
                      </div>
                      <Badge className="bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 min-w-[40px] justify-center">{w.errors}</Badge>
                    </div>
                  ))}
                </div>
                <Button
                  className="mt-4 w-full bg-rose-500 hover:bg-rose-600"
                  onClick={() => navigate('/error-book')}
                  data-testid="perf-open-errorbook-cta"
                >
                  <Zap className="w-4 h-4 mr-1.5" />Open Error Book to re-practice
                </Button>
              </div>
            )}

            {/* Daily trend */}
            {trend.length > 0 && (
              <div className="p-5 rounded-2xl border border-border bg-card" data-testid="perf-daily-trend">
                <h2 className="font-bold mb-3 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-emerald-500" />Last 30 days activity
                </h2>
                <div className="flex items-end gap-1 h-32 overflow-x-auto">
                  {trend.map((d, i) => (
                    <div key={i} className="flex flex-col items-center gap-1 min-w-[28px]" title={`${d.date}: ${d.attempts} attempts, ${d.avg_pct}% avg`}>
                      <div className="text-[9px] font-mono text-muted-foreground">{d.attempts}</div>
                      <div
                        className="w-full bg-gradient-to-t from-indigo-500 to-violet-500 rounded-t"
                        style={{ height: `${Math.max(d.avg_pct, 5)}%`, minHeight: '4px' }}
                      />
                      <div className="text-[9px] text-muted-foreground rotate-45 origin-top-left translate-x-2">
                        {d.date.slice(5)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state for newcomers */}
            {s.total_attempts === 0 && (
              <div className="text-center py-16 border-2 border-dashed border-border rounded-2xl" data-testid="perf-empty">
                <CheckCircle2 className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="font-semibold">No attempts yet</p>
                <p className="text-sm text-muted-foreground">Take a practice test and come back — your insights will show up here.</p>
                <Button className="mt-4" onClick={() => navigate('/competitive-exams')} data-testid="perf-cta-practice">
                  Browse Exams
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, color, label, value, testId }) {
  const colorMap = {
    indigo: 'text-indigo-500 bg-indigo-100 dark:bg-indigo-950/40',
    emerald: 'text-emerald-500 bg-emerald-100 dark:bg-emerald-950/40',
    amber: 'text-amber-500 bg-amber-100 dark:bg-amber-950/40',
    rose: 'text-rose-500 bg-rose-100 dark:bg-rose-950/40',
  };
  return (
    <div className="p-4 rounded-2xl border border-border bg-card" data-testid={testId}>
      <div className={`w-9 h-9 rounded-xl ${colorMap[color]} flex items-center justify-center mb-2`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-[11px] text-muted-foreground">{label}</p>
    </div>
  );
}
